<template>
  <div class="flex flex-col items-center justify-center py-12">
    <template v-if="loading">
      <div class="flex h-12 w-12 items-center justify-center">
        <svg
          class="h-12 w-12 animate-spin text-primary"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <p class="mt-4 text-sm text-[var(--plugin-gray-500)]">
        {{ t('plugin.payment.return.checkingStatus') }}
      </p>
    </template>
    <template v-else-if="fetchError">
      <div
        class="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 text-red-600"
      >
        <svg
          class="h-12 w-12"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          />
        </svg>
      </div>
      <p class="mt-4 text-center text-base font-semibold text-[var(--plugin-charcoal)]">
        {{ t('plugin.payment.return.statusUnavailable') }}
      </p>
    </template>
    <template v-else-if="status">
      <div
        class="flex h-20 w-20 items-center justify-center rounded-full"
        :class="statusIconBg"
      >
        <component :is="statusIcon" class="h-12 w-12" :class="statusIconColor" />
      </div>
      <p class="mt-4 text-center text-base font-semibold" :class="statusTextClass">
        {{ statusMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { getPaymentStatus } from '@/api/public'
import type { PaymentStatusResponse } from '@/types/api'
import { t } from '@/i18n'
import { h } from 'vue'

const POLL_INTERVALS_MS = [1000, 2000, 4000, 8000, 16000]
const MAX_POLL_DURATION_MS = 60_000

const props = defineProps<{
  reservationId: number
}>()

const loading = ref(true)
const fetchError = ref<string | null>(null)
const status = ref<PaymentStatusResponse | null>(null)

let pollTimer: ReturnType<typeof setTimeout> | null = null
let pollStartTime = 0
let pollAttemptIndex = 0

function isTerminal(res: PaymentStatusResponse): boolean {
  const s = res.status?.toLowerCase()
  if (s === 'completed' || s === 'failed' || s === 'cancelled') return true
  if (res.payed === true) return true
  return false
}

function stopPolling() {
  if (pollTimer != null) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
}

async function fetchStatus() {
  try {
    const res = await getPaymentStatus(props.reservationId)
    status.value = res
    fetchError.value = null
    loading.value = false
    if (isTerminal(res)) stopPolling()
    else scheduleNextPoll()
  } catch (e) {
    fetchError.value = e instanceof Error ? e.message : String(e)
    loading.value = false
    stopPolling()
  }
}

function scheduleNextPoll() {
  if (pollTimer != null) return
  const elapsed = Date.now() - pollStartTime
  if (elapsed >= MAX_POLL_DURATION_MS) {
    loading.value = false
    return
  }
  const intervalIndex = Math.min(pollAttemptIndex, POLL_INTERVALS_MS.length - 1)
  const delay = POLL_INTERVALS_MS[intervalIndex]
  pollTimer = setTimeout(async () => {
    pollTimer = null
    pollAttemptIndex += 1
    if (status.value && isTerminal(status.value)) return
    await fetchStatus()
    if (status.value && !isTerminal(status.value)) scheduleNextPoll()
  }, delay)
}

async function startPolling() {
  loading.value = true
  fetchError.value = null
  status.value = null
  pollStartTime = Date.now()
  pollAttemptIndex = 0
  stopPolling()
  await fetchStatus()
}

watch(
  () => props.reservationId,
  (id) => {
    if (id != null) startPolling()
  },
  { immediate: true }
)

onUnmounted(stopPolling)

const statusMessage = computed(() => {
  const s = status.value
  if (!s) return ''
  const paid = s.payed === true
  const statusText = (s.status ?? '').toLowerCase()
  if (paid || statusText === 'completed')
    return t('plugin.payment.return.successful')
  if (statusText === 'failed') return t('plugin.payment.return.failed')
  if (statusText === 'cancelled') return t('plugin.payment.return.cancelled')
  return t('plugin.payment.return.pending')
})

const statusIconBg = computed(() => {
  const s = status.value
  if (!s) return ''
  const statusText = (s.status ?? '').toLowerCase()
  const paid = s.payed === true
  if (paid || statusText === 'completed') return 'bg-green-100'
  if (statusText === 'failed') return 'bg-red-100'
  if (statusText === 'cancelled') return 'bg-orange-100'
  return 'bg-blue-100'
})

const statusIconColor = computed(() => {
  const s = status.value
  if (!s) return ''
  const statusText = (s.status ?? '').toLowerCase()
  const paid = s.payed === true
  if (paid || statusText === 'completed') return 'text-green-600'
  if (statusText === 'failed') return 'text-red-600'
  if (statusText === 'cancelled') return 'text-orange-600'
  return 'text-blue-600'
})

const statusIcon = computed(() => {
  const s = status.value
  if (!s) return () => null
  const statusText = (s.status ?? '').toLowerCase()
  const paid = s.payed === true
  if (paid || statusText === 'completed') {
    return () =>
      h('svg', { fill: 'currentColor', viewBox: '0 0 20 20' }, [
        h('path', {
          'fill-rule': 'evenodd',
          d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
          'clip-rule': 'evenodd',
        }),
      ])
  }
  if (statusText === 'failed') {
    return () =>
      h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          d: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
        }),
      ])
  }
  if (statusText === 'cancelled') {
    return () =>
      h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          d: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
        }),
      ])
  }
  return () =>
    h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        d: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
      }),
    ])
})

const statusTextClass = computed(() => {
  const s = status.value
  if (!s) return ''
  const statusText = (s.status ?? '').toLowerCase()
  const paid = s.payed === true
  if (paid || statusText === 'completed') return 'text-green-800'
  if (statusText === 'failed') return 'text-red-800'
  if (statusText === 'cancelled') return 'text-orange-800'
  return 'text-blue-800'
})
</script>
